/**
 * Endpoints de **Gestión → Compras** y **Gestión → Fletes**
 * (`/api/gestion/compras*` y `/api/gestion/fletes*`).
 *
 * Los dos listados comparten los mismos filtros de fecha, estado, serie, número y
 * proveedor, y cada uno tiene su detalle.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogosCompra,
  CompraDetalleRespuesta,
  CompraEntrada,
  ComprasRespuesta,
  DatosProveedor,
  DocumentoConsulta,
  EntradasCompraRespuesta,
  FiltrosProveedores,
  ProveedoresRespuesta,
  FiltrosCompras,
  FleteDetalleRespuesta,
  CatalogosFlete,
  FleteEntrada,
  FletesRespuesta,
  NotaCompra,
  NotaEntrada,
  PagoEntrada,
  PagosProveedorRespuesta,
  ProveedorCompra,
  ResumenPagosRespuesta,
  Transportista,
  TransportistaEntrada,
} from './compras.types';

const CLAVE = 'gestion';

export const comprasKeys = {
  compras: (filtros: FiltrosCompras) => [CLAVE, 'compras', 'listado', filtros] as const,
  compra: (id: number | null) => [CLAVE, 'compras', 'detalle', id] as const,
  entradasCompra: (id: number | null) => [CLAVE, 'compras', 'entradas', id] as const,
  proveedores: () => [CLAVE, 'compras', 'proveedores'] as const,
  fletes: (filtros: FiltrosCompras) => [CLAVE, 'fletes', 'listado', filtros] as const,
  flete: (id: number | null) => [CLAVE, 'fletes', 'detalle', id] as const,
  proveedoresDetalle: (filtros: FiltrosProveedores) =>
    [CLAVE, 'proveedores', 'listado', filtros] as const,
  catalogos: () => [CLAVE, 'compras', 'catalogos'] as const,
  resumenPagos: () => [CLAVE, 'compras', 'pagos', 'resumen'] as const,
  pagosProveedor: (id: number | null) => [CLAVE, 'compras', 'pagos', id] as const,
  notas: (id: number | null) => [CLAVE, 'compras', 'notas', id] as const,
  catalogosFlete: () => [CLAVE, 'fletes', 'catalogos'] as const,
  transportistas: () => [CLAVE, 'fletes', 'transportistas'] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const comprasApi = {
  listado: (filtros: FiltrosCompras, signal?: AbortSignal): Promise<ComprasRespuesta> =>
    apiClient.get<ComprasRespuesta>(`/api/gestion/compras${cadenaDe(filtros)}`, { signal }),

  proveedores: (signal?: AbortSignal): Promise<{ proveedores: ProveedorCompra[] }> =>
    apiClient.get<{ proveedores: ProveedorCompra[] }>('/api/gestion/compras/proveedores', {
      signal,
    }),

  detalle: (id: number, signal?: AbortSignal): Promise<CompraDetalleRespuesta> =>
    apiClient.get<CompraDetalleRespuesta>(`/api/gestion/compras/${id}`, { signal }),

  fletes: (filtros: FiltrosCompras, signal?: AbortSignal): Promise<FletesRespuesta> =>
    apiClient.get<FletesRespuesta>(`/api/gestion/fletes${cadenaDe(filtros)}`, { signal }),

  flete: (id: number, signal?: AbortSignal): Promise<FleteDetalleRespuesta> =>
    apiClient.get<FleteDetalleRespuesta>(`/api/gestion/fletes/${id}`, { signal }),

  catalogos: (signal?: AbortSignal): Promise<CatalogosCompra> =>
    apiClient.get<CatalogosCompra>('/api/gestion/compras/catalogos', { signal }),

  registrar: (datos: CompraEntrada): Promise<{ compra_id: number; mensaje: string }> =>
    apiClient.post<{ compra_id: number; mensaje: string }>('/api/gestion/compras', { body: datos }),

  actualizar: (id: number, datos: CompraEntrada): Promise<{ compra_id: number; mensaje: string }> =>
    apiClient.put<{ compra_id: number; mensaje: string }>(`/api/gestion/compras/${id}`, {
      body: datos,
    }),

  subirComprobante: (
    id: number,
    comprobante: { nombre?: string; contenido_base64: string },
  ): Promise<{ archivo: string; mensaje: string }> =>
    apiClient.post<{ archivo: string; mensaje: string }>(
      `/api/gestion/compras/${id}/comprobante`,
      { body: { comprobante } },
    ),

  anular: (id: number): Promise<{ compra_id: number; mensaje: string }> =>
    apiClient.post<{ compra_id: number; mensaje: string }>(`/api/gestion/compras/${id}/anular`, {
      body: {},
    }),

  entradasDeCompra: (id: number, signal?: AbortSignal): Promise<EntradasCompraRespuesta> =>
    apiClient.get<EntradasCompraRespuesta>(`/api/gestion/compras/${id}/entradas`, { signal }),

  registrarEntradaCompra: (
    id: number,
    datos: { producto_id: number; cantidad: number; razon?: string; fecha_entrada?: string },
  ): Promise<{ entrada_id: number; mensaje: string }> =>
    apiClient.post<{ entrada_id: number; mensaje: string }>(`/api/gestion/compras/${id}/entradas`, {
      body: datos,
    }),

  anularEntradaCompra: (detalleId: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/compras/entradas/${detalleId}`),

  proveedoresDetalle: (
    filtros: FiltrosProveedores,
    signal?: AbortSignal,
  ): Promise<ProveedoresRespuesta> =>
    apiClient.get<ProveedoresRespuesta>(`/api/gestion/proveedores${cadenaDe(filtros)}`, { signal }),

  crearProveedor: (datos: DatosProveedor): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/proveedores', { body: datos }),

  actualizarProveedor: (
    id: number,
    datos: DatosProveedor,
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.put<{ id: number; mensaje: string }>(`/api/gestion/proveedores/${id}`, { body: datos }),

  eliminarProveedor: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/proveedores/${id}`),

  consultaRucProveedor: (
    numero: string,
    signal?: AbortSignal,
  ): Promise<{ documento: DocumentoConsulta }> =>
    apiClient.get<{ documento: DocumentoConsulta }>(
      `/api/gestion/proveedores/consulta-ruc?numero=${encodeURIComponent(numero)}`,
      { signal },
    ),

  resumenPagos: (signal?: AbortSignal): Promise<ResumenPagosRespuesta> =>
    apiClient.get<ResumenPagosRespuesta>('/api/gestion/compras/pagos', { signal }),

  pagosDeProveedor: (id: number, signal?: AbortSignal): Promise<PagosProveedorRespuesta> =>
    apiClient.get<PagosProveedorRespuesta>(`/api/gestion/compras/pagos/${id}`, { signal }),

  registrarPago: (datos: PagoEntrada): Promise<{ pago_id: number; mensaje: string }> =>
    apiClient.post<{ pago_id: number; mensaje: string }>('/api/gestion/compras/pagos', {
      body: datos,
    }),

  anularPago: (id: number): Promise<{ pago_id: number; mensaje: string }> =>
    apiClient.delete<{ pago_id: number; mensaje: string }>(`/api/gestion/compras/pagos/${id}`),

  notas: (compraId: number, signal?: AbortSignal): Promise<{ notas: NotaCompra[] }> =>
    apiClient.get<{ notas: NotaCompra[] }>(`/api/gestion/compras/${compraId}/notas`, { signal }),

  crearNota: (compraId: number, datos: NotaEntrada): Promise<{ nota_id: number; mensaje: string }> =>
    apiClient.post<{ nota_id: number; mensaje: string }>(`/api/gestion/compras/${compraId}/notas`, {
      body: datos,
    }),

  actualizarNota: (
    notaId: number,
    datos: NotaEntrada,
  ): Promise<{ nota_id: number; mensaje: string }> =>
    apiClient.put<{ nota_id: number; mensaje: string }>(`/api/gestion/compras/notas/${notaId}`, {
      body: datos,
    }),

  anularNota: (notaId: number): Promise<{ nota_id: number; mensaje: string }> =>
    apiClient.delete<{ nota_id: number; mensaje: string }>(`/api/gestion/compras/notas/${notaId}`),

  catalogosFlete: (signal?: AbortSignal): Promise<CatalogosFlete> =>
    apiClient.get<CatalogosFlete>('/api/gestion/fletes/catalogos', { signal }),

  registrarFlete: (datos: FleteEntrada): Promise<{ flete_id: number; mensaje: string }> =>
    apiClient.post<{ flete_id: number; mensaje: string }>('/api/gestion/fletes', { body: datos }),

  anularFlete: (id: number): Promise<{ flete_id: number; mensaje: string }> =>
    apiClient.post<{ flete_id: number; mensaje: string }>(`/api/gestion/fletes/${id}/anular`, {
      body: {},
    }),

  actualizarPreciosFlete: (
    compraId: number,
    productos: { linea_id: number; precio_flete: number }[],
  ): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/gestion/fletes/compras/${compraId}/precios`, {
      body: { productos },
    }),

  transportistas: (signal?: AbortSignal): Promise<{ transportistas: Transportista[] }> =>
    apiClient.get<{ transportistas: Transportista[] }>('/api/gestion/fletes/proveedores-transporte', {
      signal,
    }),

  crearTransportista: (datos: TransportistaEntrada): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/fletes/proveedores-transporte', {
      body: datos,
    }),

  editarTransportista: (
    id: number,
    datos: TransportistaEntrada,
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.put<{ id: number; mensaje: string }>(
      `/api/gestion/fletes/proveedores-transporte/${id}`,
      { body: datos },
    ),

  eliminarTransportista: (id: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/fletes/proveedores-transporte/${id}`),
};

/** Listado de compras. */
export function useCompras(filtros: FiltrosCompras) {
  return useQuery({
    queryKey: comprasKeys.compras(filtros),
    queryFn: ({ signal }) => comprasApi.listado(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Proveedores para el filtro del listado. */
export function useProveedoresCompra() {
  return useQuery({
    queryKey: comprasKeys.proveedores(),
    queryFn: ({ signal }) => comprasApi.proveedores(signal),
    staleTime: 300_000,
  });
}

/** Detalle de una compra. */
export function useCompra(id: number | null) {
  return useQuery({
    queryKey: comprasKeys.compra(id),
    queryFn: ({ signal }) => comprasApi.detalle(id as number, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** Listado de fletes. */
export function useFletes(filtros: FiltrosCompras) {
  return useQuery({
    queryKey: comprasKeys.fletes(filtros),
    queryFn: ({ signal }) => comprasApi.fletes(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Detalle de un flete. */
export function useFlete(id: number | null) {
  return useQuery({
    queryKey: comprasKeys.flete(id),
    queryFn: ({ signal }) => comprasApi.flete(id as number, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** Catálogos del alta de compras. */
export function useCatalogosCompra(activo = true) {
  return useQuery({
    queryKey: comprasKeys.catalogos(),
    queryFn: ({ signal }) => comprasApi.catalogos(signal),
    enabled: activo,
    staleTime: 60_000,
  });
}

/** Registra una compra. */
export function useRegistrarCompra() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CompraEntrada) => comprasApi.registrar(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Anula una compra. */
export function useAnularCompra() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => comprasApi.anular(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Ingreso a almacén de una compra: sus líneas por ingresar y sus entradas. */
export function useEntradasDeCompra(id: number | null) {
  return useQuery({
    queryKey: comprasKeys.entradasCompra(id),
    queryFn: ({ signal }) => comprasApi.entradasDeCompra(id as number, signal),
    enabled: id !== null,
    staleTime: 5_000,
  });
}

/** Registra un ingreso a almacén de una compra. */
export function useRegistrarEntradaCompra(compraId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      producto_id: number;
      cantidad: number;
      razon?: string;
      fecha_entrada?: string;
    }) => comprasApi.registrarEntradaCompra(compraId as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'inventario'] });
    },
  });
}

/** Anula un ingreso a almacén de una compra. */
export function useAnularEntradaCompra() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (detalleId: number) => comprasApi.anularEntradaCompra(detalleId),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'inventario'] });
    },
  });
}

/* ---------------------------- Proveedores ------------------------------- */

/** Listado de proveedores con los totales de sus compras. */
export function useProveedoresDetalle(filtros: FiltrosProveedores) {
  return useQuery({
    queryKey: comprasKeys.proveedoresDetalle(filtros),
    queryFn: ({ signal }) => comprasApi.proveedoresDetalle(filtros, signal),
    placeholderData: (anterior) => anterior,
  });
}

/** Invalida el listado de proveedores y el de los selectores de compras. */
function refrescarProveedores(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'proveedores'] });
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras', 'proveedores'] });
}

/** Registra un proveedor. */
export function useCrearProveedor() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosProveedor) => comprasApi.crearProveedor(datos),
    onSuccess: () => refrescarProveedores(cliente),
  });
}

/** Edita un proveedor. */
export function useActualizarProveedor() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, datos }: { id: number; datos: DatosProveedor }) =>
      comprasApi.actualizarProveedor(id, datos),
    onSuccess: () => refrescarProveedores(cliente),
  });
}

/** Da de baja un proveedor (borrado lógico). */
export function useEliminarProveedor() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => comprasApi.eliminarProveedor(id),
    onSuccess: () => refrescarProveedores(cliente),
  });
}

/** Consulta el RUC en SUNAT (para el alta del proveedor). */
export function useConsultaRucProveedor() {
  return useMutation({
    mutationFn: (numero: string) => comprasApi.consultaRucProveedor(numero),
  });
}

/** Proveedores con saldo pendiente. */
export function useResumenPagos(activo = true) {
  return useQuery({
    queryKey: comprasKeys.resumenPagos(),
    queryFn: ({ signal }) => comprasApi.resumenPagos(signal),
    enabled: activo,
    staleTime: 15_000,
  });
}

/** Compras por pagar y pagos registrados de un proveedor. */
export function usePagosDeProveedor(id: number | null) {
  return useQuery({
    queryKey: comprasKeys.pagosProveedor(id),
    queryFn: ({ signal }) => comprasApi.pagosDeProveedor(id as number, signal),
    enabled: id !== null,
    staleTime: 10_000,
  });
}

/** Registra un pago de compras. */
export function useRegistrarPago() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: PagoEntrada) => comprasApi.registrarPago(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Anula un pago de compras. */
export function useAnularPago() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => comprasApi.anularPago(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Crea o edita una nota de compra. */
export function useGuardarNota(compraId: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: NotaEntrada & { nota_id?: number }) =>
      datos.nota_id === undefined
        ? comprasApi.crearNota(compraId as number, datos)
        : comprasApi.actualizarNota(datos.nota_id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Anula una nota de compra. */
export function useAnularNota() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (notaId: number) => comprasApi.anularNota(notaId),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Notas de crédito y débito de una compra. */
export function useNotasDeCompra(id: number | null) {
  return useQuery({
    queryKey: comprasKeys.notas(id),
    queryFn: ({ signal }) => comprasApi.notas(id as number, signal),
    enabled: id !== null,
    staleTime: 10_000,
  });
}

/** Catálogos del alta de fletes. */
export function useCatalogosFlete(activo = true) {
  return useQuery({
    queryKey: comprasKeys.catalogosFlete(),
    queryFn: ({ signal }) => comprasApi.catalogosFlete(signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Registra un flete. */
export function useRegistrarFlete() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: FleteEntrada) => comprasApi.registrarFlete(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'fletes'] });
    },
  });
}

/** Anula un flete. */
export function useAnularFlete() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => comprasApi.anularFlete(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'fletes'] });
    },
  });
}

/** Transportistas. */
export function useTransportistas(activo = true) {
  return useQuery({
    queryKey: comprasKeys.transportistas(),
    queryFn: ({ signal }) => comprasApi.transportistas(signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Crea o edita un transportista. */
export function useGuardarTransportista() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: TransportistaEntrada) =>
      datos.id === undefined
        ? comprasApi.crearTransportista(datos)
        : comprasApi.editarTransportista(datos.id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'fletes'] });
    },
  });
}

/** Elimina un transportista. */
export function useEliminarTransportista() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => comprasApi.eliminarTransportista(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'fletes'] });
    },
  });
}

/** Edita los datos del comprobante de una compra. */
export function useActualizarCompra(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CompraEntrada) => comprasApi.actualizar(id as number, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}

/** Guarda la foto del comprobante de una compra. */
export function useSubirComprobanteCompra(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (comprobante: { nombre?: string; contenido_base64: string }) =>
      comprasApi.subirComprobante(id as number, comprobante),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'compras'] });
    },
  });
}
